import type { CardView } from "../api/types.js";

/**
 * DCTA-131 (iPhone #3; ADR-0014, rulings PQ-04/05) — what is still due on a credit card's statement, as the bank
 * says it. A statement's own balance and minimum are facts of the day it was issued and never move; after a
 * payment the card screens printed them beside the bank's "Nothing is owed" ("Statement S$412.00"). The card read
 * now carries what is still DUE on it (`statement_due_now`, `minimum_due_now`, from the bill row that payments
 * reduce): every card screen reads those. An older bank without them leaves the statement's own figures, and
 * nothing is claimed paid. Nothing here is computed: the figures are the bank's strings; only whether they
 * differ from the statement's is read (in cents, so "412.0" and "412.00" are one figure).
 */
export interface CardDue {
  /** Still due on the statement (decimal string), or undefined when the card has no statement. */
  readonly statement: string | undefined;
  /** The minimum still due on it, or undefined. */
  readonly minimum: string | undefined;
  /** Something has been paid toward the statement since it was issued. */
  readonly paid: boolean;
}

const CENTS = /^(\d+)(?:\.(\d{1,2}))?$/;

/** A bank decimal string in cents; undefined for anything that is not one (never guessed). */
function cents(value: string | undefined): bigint | undefined {
  const m = value === undefined ? null : CENTS.exec(value);
  if (m === null) return undefined;
  return BigInt(m[1] as string) * 100n + BigInt((m[2] ?? "").padEnd(2, "0"));
}

const differs = (a: string | undefined, b: string | undefined): boolean => {
  const x = cents(a);
  const y = cents(b);
  return x !== undefined && y !== undefined && x !== y;
};

export function cardDue(c: CardView): CardDue {
  return {
    statement: c.statement_due_now ?? c.statement_balance,
    minimum: c.minimum_due_now ?? c.minimum_due,
    paid: differs(c.statement_due_now, c.statement_balance) || differs(c.minimum_due_now, c.minimum_due),
  };
}

/** A figure greater than zero ("0.00" is not due; an unreadable figure is never treated as due). */
export function isDue(value: string | undefined): boolean {
  const c = cents(value);
  return c !== undefined && c > 0n;
}
